import { RgbaColor, colord, extend } from 'colord';
import { useEffect, useState } from 'react';
import { RgbaColorPicker } from 'react-colorful';
import styled from 'styled-components';
import { NumberInput } from './NumberInput';

import namesPlugin from 'colord/plugins/names';
import { extractUniqueColorsFromJSON } from '../../../functions/utils/extractUniqueColorsFromJSON';
import { useScene } from '../../../hooks';

extend([namesPlugin]);

export const ColorPicker = ({
  value,
  onChange,

  extended,
  setExtended,
}: {
  value?: string;
  onChange: (newValue: string | undefined) => void;

  extended?: boolean;
  setExtended?: (newValue: boolean) => void;
}) => {
  const scene = useScene();
  const [isExtended, setIsExtended] = useState(extended || false);
  const [rgba, setRgba] = useState(value ? colord(value).toRgb() : undefined);

  useEffect(() => {
    onChange(rgba ? colord(rgba).toHex() : undefined);
  }, [rgba]);

  const presetColors = extractUniqueColorsFromJSON(
    scene.exportLauncherConfigJSON()
  );
  const backgroundColors = presetColors.backgroundColor.sort();
  const fontColors = presetColors.color.sort();
  const borderColors = presetColors.borderColor.sort();

  return (
    <Container>
      <ExtendButton
        $isOpen={isExtended}
        onClick={() => {
          setIsExtended(!isExtended);
          setExtended && setExtended(!isExtended);
        }}
      >
        <i className="icon-down-open-mini" />
      </ExtendButton>

      {isExtended && (
        <ExtendContainer>
          <Label>Background colors</Label>
          <Swatches>
            {backgroundColors.map((presetColor, i) => (
              <ColorPreview key={`backgroundColors-swatch-${i}`}>
                <Checkboard />
                <ColorFill
                  $color={presetColor}
                  onClick={() => setRgba(colord(presetColor).toRgb())}
                />
              </ColorPreview>
            ))}
          </Swatches>

          <Label>Font colors</Label>
          <Swatches>
            {fontColors.map((presetColor, i) => (
              <ColorPreview key={`backgroundColors-swatch-${i}`}>
                <Checkboard />
                <ColorFill
                  $color={presetColor}
                  onClick={() => setRgba(colord(presetColor).toRgb())}
                />
              </ColorPreview>
            ))}
          </Swatches>

          <Label>Border colors</Label>
          <Swatches>
            {borderColors.map((presetColor, i) => (
              <ColorPreview key={`backgroundColors-swatch-${i}`}>
                <Checkboard />
                <ColorFill
                  $color={presetColor}
                  onClick={() => setRgba(colord(presetColor).toRgb())}
                />
              </ColorPreview>
            ))}
          </Swatches>
        </ExtendContainer>
      )}

      <RgbaColorPicker
        color={rgba}
        onChange={(newValue) => setRgba(newValue)}
      />
      <Inputs>
        <NumberInput
          id={`color-picker-r-input`}
          label="r"
          value={rgba?.r}
          defaultValue={0}
          onChange={(newNumberValue) =>
            setRgba((rgba) => ({ ...rgba, r: newNumberValue }) as RgbaColor)
          }
          centered
        />
        <NumberInput
          id={`color-picker-g-input`}
          label="g"
          value={rgba?.g}
          defaultValue={0}
          onChange={(newNumberValue) =>
            setRgba((rgba) => ({ ...rgba, g: newNumberValue }) as RgbaColor)
          }
          centered
        />
        <NumberInput
          id={`color-picker-b-input`}
          label="b"
          value={rgba?.b}
          defaultValue={0}
          onChange={(newNumberValue) =>
            setRgba((rgba) => ({ ...rgba, b: newNumberValue }) as RgbaColor)
          }
          centered
        />
      </Inputs>
      <NumberInput
        id={`color-picker-a-input`}
        label="a"
        value={rgba?.a}
        defaultValue={0}
        onChange={(newNumberValue) =>
          setRgba((rgba) => ({ ...rgba, a: newNumberValue }) as RgbaColor)
        }
        centered
      />
    </Container>
  );
};

const Container = styled.div`
  width: 200px;
  padding: 5px;
  border-radius: 10px;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 5px 15px;

  label {
    padding-left: 3px;
    padding-right: 8px;
  }

  color: var(--text-color);
  font-family: 'Inter';
  font-size: 12px;
`;

const Inputs = styled.div`
  width: 200px;
  margin: 10px 0;
  display: flex;
  gap: 5px;

  & > * {
    width: 33%;
  }
`;

const ExtendButton = styled.button<{ $isOpen: boolean }>`
  cursor: pointer;
  width: 100%;
  height: 16px;
  margin-bottom: 10px;

  display: flex;
  justify-content: center;
  align-items: center;

  background-color: var(--panel-bg-color-secondary);
  border-radius: 8px;

  &:hover {
    background-color: var(--panel-bg-color-hover);
  }

  & > i {
    transform: rotate(90deg);
    transition: transform 0.2s ease;
  }

  ${({ $isOpen }) =>
    $isOpen &&
    `
    & > i {
      transform: rotate(-90deg);
    }
  `}
`;

const ExtendContainer = styled.div`
  user-select: none;
  position: absolute;
  top: 0;
  left: -100%;

  width: 200px;
  padding: 5px;
  border-radius: 10px;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 5px 15px;
  background-color: var(--panel-bg-color-secondary);

  label {
    padding-left: 3px;
    padding-right: 8px;
  }

  color: var(--text-color);
  font-family: 'Inter';
  font-size: 12px;
`;

const Label = styled.span`
  display: block;
  padding: 0 10px;
  color: var(--text-color-secondary);
`;

const Swatches = styled.div`
  padding: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

const ColorPreview = styled.div`
  box-sizing: border-box;
  cursor: pointer;
  position: relative;
  width: 25px;
  height: 25px;
  border-radius: 5px;

  overflow: hidden;

  transition: transform 0.2s ease;
  &:hover {
    transform: scale(1.2);
  }
`;

const Checkboard = styled.div`
  box-sizing: border-box;
  width: 100%;
  height: 100%;

  background: url('data:image/svg+xml;utf8,<svg preserveAspectRatio="none"  viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg"><rect x="0" y="0" width="5" height="5" fill="grey" /><rect x="5" y="5" width="5" height="5" fill="grey" /><rect x="5" y="0" width="5" height="5" fill="white" /><rect x="0" y="5" width="5" height="5" fill="white" /></svg>')
    0 0/15px 15px round;
`;

const ColorFill = styled.div<{ $color: string }>`
  box-sizing: border-box;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  ${({ $color }) => $color && `background-color: ${$color};`}
`;
